import { useMemo } from "react";

import type { WorkspaceTab } from "./WorkspaceTabNav";

import { usePageLayoutShellAccess } from "@/components/layout/usePageLayoutShellAccess";
import {
  findAdminNavLocation,
  getAdminNavPageTabs,
  isAdminNavPageOwnRoute,
} from "@/routing/adminNavModelQueries";

export interface AdminNavPageTabStrip {
  activePath: string;
  ariaLabel: string;
  tabs: readonly WorkspaceTab[];
}

/** A stable empty list for a chrome with no strip (e.g. its tab logging). */
export const NO_WORKSPACE_TABS: readonly WorkspaceTab[] = [];

/**
 * A workspace chrome's tab strip, from Kate's §11 navigation table
 * (adminNavModel) rather than a hand-written list: the current page's tabs
 * that have their own address and that this admin may open (F-211, F-212).
 * In-page tabs belong to the page's own tab list. A route outside any page
 * (search-only, e.g. Business types) or a page with fewer than two such tabs
 * shows no strip (null). The active tab is the route itself, or the page's
 * first tab on the page's own address; a member route (a record or tool)
 * marks none.
 */
export function useAdminNavPageTabStrip(
  pathname: string,
): AdminNavPageTabStrip | null {
  const shellAccess = usePageLayoutShellAccess();
  return useMemo(() => {
    const location = findAdminNavLocation(pathname);
    const page = location?.page;
    if (!location || !page) return null;
    const tabs = getAdminNavPageTabs(page, {
      adminV2FoundationsEnabled: shellAccess.adminV2FoundationsEnabled,
      permissionKeys: shellAccess.permissionKeys,
      menuVisibility: shellAccess.effectiveMenuVisibility,
    })
      .filter((tab) => !tab.inPage)
      .map(({ label, path }) => ({ label, path }));
    if (tabs.length < 2) return null;
    const current = location.tab
      ? tabs.find((tab) => tab.path === location.route.path)
      : isAdminNavPageOwnRoute(page, location.route.id)
        ? tabs[0]
        : undefined;
    return {
      activePath: current?.path ?? "",
      ariaLabel: `${page.label} tabs`,
      tabs,
    };
  }, [
    pathname,
    shellAccess.adminV2FoundationsEnabled,
    shellAccess.effectiveMenuVisibility,
    shellAccess.permissionKeys,
  ]);
}
